import { useEffect, useRef, useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Archive, Flag, MessageCircle, Pencil, Plus, RotateCcw, Send, Square } from "lucide-react";
import { clsx } from "clsx";
import { supabase } from "@/lib/supabase";
import { useSnapshotActual } from "@/lib/snapshot";
import { useToast } from "@/components/ui/Toast";
import { Boton, Cargando, Dialogo, Input, Vacio } from "@/components/ui";
import { useChatStream, type ChatMsg } from "./useChatStream";
import { Espera, NOMBRE_HERRAMIENTA } from "./Espera";
import { useAuthOptional } from "@/auth/AuthProvider";
import { Markdown } from "./Markdown";
import { motivoClave } from "@/lib/motivos";
import { DialogoMotivo } from "@/features/reportes/DialogoMotivo";

// César Colín, asesor de Revolution Wealth Management. El miembro decide si le escribe; el asesor IA nunca lo empuja.
const WHATSAPP_ASESOR_RWM = `https://wa.me/525554699709?text=${encodeURIComponent("Hola César, vengo de Wealth Nation y quiero hablar con mi asesor de Revolution Wealth Management.")}`;

const SUGERENCIAS = ["¿Cómo voy?", "¿Por qué mi objetivo sale 0% financiado?", "¿Qué me conviene mover primero?", "¿Qué le llevo a mi asesor?"];

export function Chat() {
  const snap = useSnapshotActual();
  const toast = useToast();
  const chat = useChatStream();
  const uso = useAuthOptional()?.estado?.usage;
  const [texto, setTexto] = useState("");
  const [reportando, setReportando] = useState<ChatMsg | null>(null);
  const [renombrando, setRenombrando] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const convs = useQuery({ queryKey: ["conversations"], queryFn: async () => { const { data } = await supabase.from("v_my_conversations").select("id, title, updated_at, message_count").eq("archived", false).order("updated_at", { ascending: false }).limit(10); return (data ?? []) as { id: string; title: string | null; updated_at: string; message_count: number }[]; } });

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [chat.messages]);
  useEffect(() => { if (!chat.conversationId && convs.data?.[0]) void chat.load(convs.data[0].id); /* abre la última */ // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convs.data]);
  useEffect(() => { if (!chat.streaming) void convs.refetch(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.streaming]);

  async function enviar(e?: FormEvent, t?: string) {
    e?.preventDefault();
    const msg = (t ?? texto).trim();
    if (!msg || chat.streaming) return;
    setTexto("");
    const err = await chat.send(msg);
    if (err) toast.error(err);
  }

  async function reportar(motivo: string, detalle: string) {
    if (!reportando?.dbId) { toast.error("Espera a que termine la respuesta para reportarla."); return; }
    const { error } = await supabase.rpc("report_response", { p_message_id: reportando.dbId, p_report_id: null, p_reason: motivo, p_detail: detalle });
    if (error) toast.error("No pude enviar el reporte."); else toast.ok("Gracias. Lo revisamos.");
    setReportando(null);
  }

  async function guardarTitulo() {
    if (!chat.conversationId || renombrando === null) return;
    const { error } = await supabase.from("conversations").update({ title: renombrando.trim().slice(0, 80) || null }).eq("id", chat.conversationId);
    setRenombrando(null);
    if (error) toast.error("No pude renombrarla."); else void convs.refetch();
  }
  async function archivar() {
    if (!chat.conversationId) return;
    const { error } = await supabase.from("conversations").update({ archived: true }).eq("id", chat.conversationId);
    if (error) { toast.error("No pude archivarla."); return; }
    chat.nueva();
    void convs.refetch();
    toast.ok("Conversación archivada.");
  }

  if (snap.isLoading) return <Cargando />;
  if (!snap.data) return <Vacio titulo="Primero tu foto" texto="Llena tus datos aquí o sube tu MODELO WX para que el asesor tenga tus números." accion={<Link href="/empezar"><Boton>Empezar</Boton></Link>} />;

  return (
    <div className="flex h-[calc(100dvh-7.5rem)] flex-col md:h-[calc(100dvh-6rem)]">
      <div className="flex items-center justify-between gap-2 pb-2">
        <select className="h-9 max-w-[60%] rounded-md border border-borde bg-white px-2 text-sm" value={chat.conversationId ?? ""} onChange={(e) => void chat.load(e.target.value || null)}>
          <option value="">Conversación nueva</option>
          {convs.data?.map((c) => <option key={c.id} value={c.id}>{c.title ?? "Sin título"}</option>)}
        </select>
        <div className="flex gap-1">
          {chat.conversationId && !chat.streaming && <>
            <Boton variante="fantasma" tamano="sm" aria-label="Renombrar conversación" onClick={() => setRenombrando(convs.data?.find((c) => c.id === chat.conversationId)?.title ?? "")}><Pencil className="h-4 w-4" aria-hidden /></Boton>
            <Boton variante="fantasma" tamano="sm" aria-label="Archivar conversación" onClick={() => void archivar()}><Archive className="h-4 w-4" aria-hidden /></Boton>
          </>}
          <Boton variante="secundario" tamano="sm" onClick={chat.nueva}><Plus className="h-4 w-4" aria-hidden /> Nueva</Boton>
        </div>
      </div>
      {uso?.warn && <p className="mb-2 rounded-md bg-crema px-3 py-2 text-xs text-negro" role="status">{uso.cap_reached ? "Tu asesor descansa hasta el día 1 del mes. Tu foto y tus reportes siguen aquí." : `Has usado el ${Math.round(uso.pct * 100)}% de tu asesor este mes; se renueva el día 1.`}</p>}
      <p className="pb-2 text-xs text-tinta-suave">Herramienta educativa con los supuestos del programa. No es asesoría ni recomendación de inversión. Verifica cualquier cifra antes de decidir.</p>
      <div className="flex-1 space-y-3 overflow-y-auto pr-1">
        {chat.messages.length === 0 && (
          <div className="flex flex-col gap-2 pt-6">
            <p className="text-sm text-tinta-suave">Tu asesor ya tiene tu foto. Pregúntale lo que quieras sobre tu diseño patrimonial.</p>
            <p className="text-xs text-tinta-suave">Lee toda tu foto antes de contestar, así que cada respuesta tarda unos segundos y va apareciendo poco a poco.</p>
            <div className="flex flex-wrap gap-2">{SUGERENCIAS.map((s) => <button key={s} type="button" className="rounded-full border border-borde bg-white px-3 py-1.5 text-sm hover:border-primary" onClick={() => void enviar(undefined, s)}>{s}</button>)}</div>
          </div>
        )}
        {chat.messages.map((m) => (
          <div key={m.id} className={clsx("flex", m.role === "user" ? "justify-end" : "justify-start")}>
            <div className={clsx("max-w-[92%] rounded-2xl px-4 py-2.5 text-[15px] leading-snug", m.role === "user" ? "bg-primary text-white" : "border border-borde bg-white text-tinta", m.status === "error" && "border-red-300 bg-error-suave text-red-900")}>
              {m.role === "assistant" && m.tools.length > 0 && (m.text || m.status !== "streaming") && <div className="mb-1 text-xs text-tinta-suave">{m.tools.map((t) => NOMBRE_HERRAMIENTA[t] ?? t).join(" · ")}</div>}
              {m.role === "assistant" && m.status === "streaming" && !m.text ? <Espera m={m} /> : m.role === "assistant" ? <Markdown text={m.text} /> : <span className="whitespace-pre-wrap">{m.text}</span>}
              {m.role === "assistant" && m.status === "error" && !chat.streaming && m.id === chat.messages[chat.messages.length - 1]?.id && (
                <button type="button" className="mt-2 inline-flex min-h-9 items-center gap-1 rounded-md border border-red-300 bg-white px-3 text-sm text-red-900 hover:bg-error-suave" onClick={() => void chat.retry().then((err) => { if (err) toast.error(err); })}><RotateCcw className="h-3.5 w-3.5" aria-hidden /> Reintentar</button>
              )}
              {m.role === "assistant" && m.status === "ok" && m.text && (
                <button type="button" className="mt-1 inline-flex items-center gap-1 text-xs text-tinta-suave hover:text-tinta" onClick={() => setReportando(m)}><Flag className="h-3 w-3" aria-hidden /> Reportar esta respuesta</button>
              )}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      <div className="sticky bottom-0 border-t border-borde bg-fondo pt-2">
        <a href={WHATSAPP_ASESOR_RWM} target="_blank" rel="noopener noreferrer" className="mb-2 inline-flex min-h-11 items-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-white transition-colors hover:bg-primary-hover"><MessageCircle className="h-4 w-4" aria-hidden /> Hablar con mi asesor de Revolution Wealth Management</a>
      <form onSubmit={enviar} className="flex gap-2">
        <input className="h-11 flex-1 rounded-md border border-borde bg-white px-3 text-base" placeholder={chat.streaming ? "El asesor está respondiendo…" : "Pregúntale a tu asesor…"} value={texto} onChange={(e) => setTexto(e.target.value)} maxLength={2000} disabled={chat.streaming} />
        {chat.streaming ? <Boton type="button" variante="secundario" onClick={chat.stop} aria-label="Detener"><Square className="h-4 w-4" aria-hidden /></Boton> : <Boton type="submit" disabled={!texto.trim()} aria-label="Enviar"><Send className="h-4 w-4" aria-hidden /></Boton>}
      </form>
      </div>
      <Dialogo abierto={renombrando !== null} onCerrar={() => setRenombrando(null)} titulo="Renombrar conversación">
        <form className="flex flex-col gap-3 p-5" onSubmit={(e) => { e.preventDefault(); void guardarTitulo(); }}>
          <Input autoFocus value={renombrando ?? ""} maxLength={80} onChange={(e) => setRenombrando(e.target.value)} />
          <Boton type="submit">Guardar</Boton>
        </form>
      </Dialogo>
      <DialogoMotivo abierto={!!reportando} onCerrar={() => setReportando(null)} onElegir={(mo) => void reportar(motivoClave(mo), reportando?.text.slice(0, 500) ?? "")} />
    </div>
  );
}
